import { OrdersRepository } from '../repositories/orders.repository'
import { UpdateAdditionalServicesPayloadT } from '../types/additional_services.types'
import {
    DownloadInsuranceResponse,
    GetCostFilesT,
    GetInvoiceProblemsT,
    GetInvoiceStatusHistory,
    GetPeriodsT,
    GetShortcomingFilesT,
    GetTemperatureRegimesT,
} from '../types/create_order.types'
import {
    ArrestInvoicePayload,
    CallcenterOrderPayload,
    FTLOrderPayloadI,
    FTLOrdersGetListParamsT,
    FTLOrdersGetListResponseT,
    GetCourierAppDeliveryCancelReasonsResponse,
    GetFtlOrderDelayResponse,
    GetFTLOrderResponseT,
    GetInvoiceCouriersResponseT,
    GetInvoiceTripsInfo,
    OrderPayloadI,
    OrdersDownloadFTLPayloadT,
    OrdersDownloadFTLResponseT,
    OrdersGetInvoiceForPrintResponseT,
    OrdersGetInvoiceHistoryResponseT,
    OrdersGetInvoiceResponseT,
    OrdersGetInvoiceStatusesT,
    OrdersGetListParamsT,
    OrdersGetListResponseT,
    OrdersGetOrderForPrintResponseT,
    OrdersGetOrderInvoicesResponseT,
    OrdersGetOrderResponseT,
    OrdersGetPreviousStatuses,
    RestoreInvoicePayload,
    SendDispatcherCommentInvoiceT,
    SendDispatcherCommentOrderT,
    SetDeclaredPricePayloadT,
    setNewPredictDateParams,
    UpdateOrderPayloadI,
} from '../types/orders.types'

const ordersRepository = new OrdersRepository()

export class OrdersService {
    async getList(params?: OrdersGetListParamsT): Promise<OrdersGetListResponseT> {
        const response = await ordersRepository.getList(params)
        return response.data
    }

    async getFTLOrders(params?: FTLOrdersGetListParamsT): Promise<FTLOrdersGetListResponseT> {
        const response = await ordersRepository.getFTLOrders(params)
        return response.data
    }

    async getOrder(order_id: number): Promise<OrdersGetOrderResponseT> {
        const response = await ordersRepository.getOrder(order_id)
        return response.data
    }

    async getFTLOrder(order_id: number): Promise<GetFTLOrderResponseT> {
        const response = await ordersRepository.getFTLOrder(order_id)
        return response.data
    }

    async getFtlOrderDelay(order_id: number): Promise<GetFtlOrderDelayResponse> {
        const response = await ordersRepository.getFtlOrderDelay(order_id)
        return response.data
    }

    async setCommentFtlOrder(order_id: number, commentId: number, stateId: number): Promise<void> {
        const response = await ordersRepository.setCommentFtlOrder(order_id, commentId, stateId)
        return response.data
    }

    async getOrderInvoices(
        order_id: number,
        params: any
    ): Promise<OrdersGetOrderInvoicesResponseT> {
        const response = await ordersRepository.getOrderInvoices(order_id, params)
        return response.data
    }

    async getOrderInvoicesForPrint(order_id: number): Promise<OrdersGetOrderInvoicesResponseT> {
        const response = await ordersRepository.getOrderInvoicesForPrint(order_id)
        return response.data
    }

    async getOrderForPrint(order_id: number): Promise<OrdersGetOrderForPrintResponseT> {
        const response = await ordersRepository.getOrderForPrint(order_id)
        return response.data
    }

    async getInvoice(invoice_id: number): Promise<OrdersGetInvoiceResponseT> {
        const response = await ordersRepository.getInvoice(invoice_id)
        return response.data
    }

    async getInvoiceTripsInfo(invoice_id: number): Promise<GetInvoiceTripsInfo> {
        const response = await ordersRepository.getInvoiceTripsInfo(invoice_id)
        return response.data
    }

    async getLTLInvoiceForPrint(invoice_id: number): Promise<OrdersGetInvoiceResponseT> {
        const response = await ordersRepository.getLTLInvoiceForPrint(invoice_id)
        return response.data
    }

    async getInvoiceByInvoiceNumber(invoice_number: number): Promise<OrdersGetInvoiceResponseT> {
        const response = await ordersRepository.getInvoiceByInvoiceNumber(invoice_number)
        return response.data
    }

    async updateInvoiceNumber(invoice_id: number, invoice_number: string): Promise<void> {
        const response = await ordersRepository.updateInvoiceNumber(invoice_id, invoice_number)
        return response.data
    }

    async setNewPredictDate(invoice_id: number, params: setNewPredictDateParams): Promise<void> {
        const response = await ordersRepository.setNewPredictDate(invoice_id, params)
        return response.data
    }

    async verify(invoice_id: number, verify: number): Promise<void> {
        const response = await ordersRepository.verify(invoice_id, verify)
        return response.data
    }

    async setPreviousStatus(invoice_id: number, code: string): Promise<void> {
        const response = await ordersRepository.setPreviousStatus(invoice_id, code)
        return response.data
    }

    async sendCommentOrder(payload: SendDispatcherCommentOrderT): Promise<void> {
        const response = await ordersRepository.sendCommentOrder(payload)
        return response.data
    }

    async importActNumbers(payload: FormData): Promise<void> {
        await ordersRepository.importActNumbers(payload)
    }

    async sendCommentInvoice(payload: SendDispatcherCommentInvoiceT): Promise<void> {
        const response = await ordersRepository.sendCommentInvoice(payload)
        return response.data
    }

    async getInvoiceForPrint(invoice_number: string): Promise<OrdersGetInvoiceForPrintResponseT> {
        const response = await ordersRepository.getInvoiceForPrint(invoice_number)
        return response.data
    }

    async getInvoiceDeliveryHistory(
        invoice_number: string
    ): Promise<OrdersGetInvoiceHistoryResponseT> {
        const response = await ordersRepository.getInvoiceDeliveryHistory(invoice_number)
        return response.data
    }

    async cancelOrder(order_id: number): Promise<void> {
        const response = await ordersRepository.cancelOrder(order_id)
        return response.data
    }

    async cancelInvoice(invoice_id: number): Promise<void> {
        const response = await ordersRepository.cancelInvoice(invoice_id)
        return response.data
    }

    async arrestInvoice(invoice_id: number, payload: ArrestInvoicePayload): Promise<void> {
        const response = await ordersRepository.arrestInvoice(invoice_id, payload)
        return response.data
    }

    async approveSelfDelivery(invoice_id: number): Promise<void> {
        const response = await ordersRepository.approveSelfDelivery(invoice_id)
        return response.data
    }

    async approveDelivery(invoice_id: number, payload: FormData): Promise<void> {
        const response = await ordersRepository.approveDelivery(invoice_id, payload)
        return response.data
    }

    async restoreInvoice(
        invoice_id: number,
        payload: RestoreInvoicePayload | undefined
    ): Promise<void> {
        const response = await ordersRepository.restoreInvoice(invoice_id, payload)
        return response.data
    }

    async createOrder(payload: OrderPayloadI): Promise<void> {
        await ordersRepository.createOrder(payload)
    }

    async updateOrder(order_id: number, payload: UpdateOrderPayloadI): Promise<void> {
        await ordersRepository.updateOrder(order_id, payload)
    }

    async createFTLOrder(payload: FTLOrderPayloadI): Promise<void> {
        await ordersRepository.createFTLOrder(payload)
    }

    async createCallcenterOrder(payload: CallcenterOrderPayload): Promise<void> {
        await ordersRepository.createCallcenterOrder(payload)
    }

    async getInvoiceStatuses(invoice_number: string): Promise<OrdersGetInvoiceStatusesT> {
        const response = await ordersRepository.getInvoiceStatuses(invoice_number)
        return response.data
    }

    async getPreviousStatuses(): Promise<OrdersGetPreviousStatuses> {
        const response = await ordersRepository.getPreviousStatuses()
        return response.data
    }

    async setDeclaredPrice(id: number, payload: SetDeclaredPricePayloadT): Promise<void> {
        const response = await ordersRepository.setDeclaredPrice(id, payload)
        return response.data
    }

    async updateOrderAdditionalServices(
        id: number,
        payload: UpdateAdditionalServicesPayloadT
    ): Promise<void> {
        const response = await ordersRepository.updateOrderAdditionalServices(id, payload)
        return response.data
    }

    async createOrderFromExcel(payload: FormData): Promise<void> {
        const response = await ordersRepository.createOrderFromExcel(payload)
        return response.data
    }

    async createMultipleOrderFromExcel(payload: FormData): Promise<void> {
        const response = await ordersRepository.createMultipleOrderFromExcel(payload)
        return response.data
    }

    async createMultipleFTLOrderFromExcel(payload: FormData): Promise<void> {
        const response = await ordersRepository.createMultipleFTLOrderFromExcel(payload)
        return response.data
    }

    async exportExcelFTL(params: OrdersDownloadFTLPayloadT): Promise<Blob> {
        const response = await ordersRepository.exportExcelFTL(params)
        return response.data
    }

    async exportExcelCompanyFTL(
        params: OrdersDownloadFTLPayloadT
    ): Promise<OrdersDownloadFTLResponseT> {
        const response = await ordersRepository.exportExcelCompanyFTL(params)
        return response.data
    }

    async getTemperatureRegimes(): Promise<GetTemperatureRegimesT> {
        const response = await ordersRepository.getTemperatureRegimes()
        return response.data
    }

    async getPeriods(): Promise<GetPeriodsT> {
        const response = await ordersRepository.getPeriods()
        return response.data
    }

    async getInvoiceProblems(invoiceId: number): Promise<GetInvoiceProblemsT> {
        const response = await ordersRepository.getInvoiceProblems(invoiceId)
        return response.data
    }

    async getInvoiceStatusHistory(invoiceNumber: string): Promise<GetInvoiceStatusHistory> {
        const response = await ordersRepository.getInvoiceStatusHistory(invoiceNumber)
        return response.data
    }

    async saveBox(invoiceId: number, packCode: string): Promise<void> {
        const response = await ordersRepository.saveBox(invoiceId, packCode)
        return response.data
    }

    async getShortcomingFiles(orderId: number): Promise<GetShortcomingFilesT> {
        const response = await ordersRepository.getShortcomingFiles(orderId)
        return response.data
    }

    async getCostFilesDeliveries(invoiceId: number): Promise<GetCostFilesT> {
        const response = await ordersRepository.getCostFilesDeliveries(invoiceId)
        return response.data
    }

    async getCostFilesTakes(orderId: number): Promise<GetCostFilesT> {
        const response = await ordersRepository.getCostFilesTakes(orderId)
        return response.data
    }

    async downloadInsurance(fileUuid: string): Promise<DownloadInsuranceResponse> {
        const response = await ordersRepository.downloadInsurance(fileUuid)
        return response.data
    }

    async resendInvoiceDeliveryStatusToOneC(invoiceId: number): Promise<void> {
        const response = await ordersRepository.resendInvoiceDeliveryStatusToOneC(invoiceId)
        return response.data
    }

    async getInvoiceCouriers(invoiceId: number): Promise<GetInvoiceCouriersResponseT> {
        const response = await ordersRepository.getInvoiceCouriers(invoiceId)
        return response.data
    }

    async getCancelReasons(companyId: number): Promise<GetCourierAppDeliveryCancelReasonsResponse> {
        const response = await ordersRepository.getCancelReasons(companyId)
        return response.data
    }

    async updateProperties(invoiceId: number): Promise<void> {
        const response = await ordersRepository.updateProperties(invoiceId)
        return response.data
    }
}
